import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@project/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Badge } from "@project/components/ui/badge";
import { Switch } from "@project/components/ui/switch";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Eye,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { formatDate } from "@project/lib/date-format";
import { exportApiRowsToCsv } from "@project/lib/csv";
import { fetchAllListData } from "@project/services/admin/list-api-helpers";
import { CsvExportButton } from "@project/admin/components/csv-export-button";
import { cn, resolveMediaUrl } from "@project/lib/utils";
import type { Field } from "@project/admin/components/resource-table";
import {
  contactFormStatusClass,
  formatContactFormStatusLabel,
} from "@project/types/contact-form";
import {
  feedbackStatusClass,
  formatFeedbackStatusLabel,
} from "@project/types/feedback";
import {
  donationRequestStatusClass,
  formatDonationRequestStatusLabel,
  isDonationRequestStatus,
} from "@project/types/item-donation";
import {
  formatPaymentStatusLabel,
  paymentStatusBadgeClass,
} from "@project/types/donation-detail";
import { TablePagination } from "@project/admin/components/table-pagination";
import { DeleteConfirmDialog } from "@project/admin/components/delete-confirm-dialog";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { TagsInput } from "@project/admin/components/tags-input";
import { StarRating } from "@project/components/star-rating";
import type {
  ApiPagination,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";

export type LookupOption = { value: string; label: string; secondary?: string };

export type ApiResourceExtraColumn<T extends { id: number }> = {
  id: string;
  label: string;
  headerClassName?: string;
  cellClassName?: string;
  render: (row: T, ctx: { refresh: () => void }) => ReactNode;
};

const DEFAULT_PAGE_SIZE = 20;

export type ApiResourceTableProps<T extends { id: number }> = {
  title: string;
  fields: Field[];
  labelField?: string;
  searchFields?: string[];
  list: (query: ListQuery) => Promise<PaginatedResult<T>>;
  create?: (payload: Record<string, unknown>) => Promise<T | undefined>;
  update?: (
    id: number,
    payload: Record<string, unknown>
  ) => Promise<T | undefined>;
  remove?: (id: number) => Promise<void>;
  /** When set, nested child rows call this instead of `remove` (e.g. PATCH parent children). */
  removeChild?: (
    parentId: number,
    childId: number,
    parent: T,
    child: T
  ) => Promise<void>;
  lookupOptions?: Record<string, LookupOption[]>;
  lookupLoaders?: Record<string, () => Promise<LookupOption[]>>;
  lookupSearchLoaders?: Record<
    string,
    (query: string) => Promise<LookupOption[]>
  >;
  defaultValues?: Record<string, unknown>;
  pageSize?: number;
  /** Defaults to true — CSV exports the full API result for the current filters. */
  enableCsvExport?: boolean;
  csvFileName?: string;
  /** When editing, only these fields are editable and included in the update payload. */
  editOnlyFields?: string[];
  /** Status filter dropdown — values are sent as `status` in list queries (omit when "all"). */
  statusFilterOptions?: { value: string; label: string }[];
  /** Initial value for the status filter dropdown. Defaults to `"all"`. */
  initialStatusFilter?: string;
  /** Sum this numeric field across loaded rows and show a total in the toolbar. */
  totalField?: string;
  /** Replace the default form field grid with custom form content. */
  renderForm?: (props: {
    editing: Partial<T> | null;
    setEditing: React.Dispatch<React.SetStateAction<Partial<T> | null>>;
    isEditMode: boolean;
  }) => ReactNode;
  /** Build the save payload from form state (used with renderForm). */
  buildPayload?: (editing: Partial<T>) => Record<string, unknown> | null;
  /** Map an API record into form state when editing. */
  mapRecordToForm?: (record: T) => Partial<T>;
  getDialogTitle?: (editing: Partial<T> | null, title: string) => string;
  dialogClassName?: string;
  addButtonLabel?: string;
  /** Extra toolbar controls rendered next to CSV / Add. */
  toolbarActions?: ReactNode;
  onView?: (row: T) => void;
  renderRowActions?: (row: T, ctx: { refresh: () => void }) => ReactNode;
  /** Extra columns rendered before the Actions column. */
  extraColumns?: ApiResourceExtraColumn<T>[];
  /** Fetch full record when editing (e.g. nested relations not in list rows). */
  loadRecordForEdit?: (id: number) => Promise<T | undefined>;
  /** Add date filtering (start_date/end_date) inputs to the table toolbar. */
  enableDateFilter?: boolean;
  /** Custom renderer for expanded row content spanning all columns. */
  renderExpandableRow?: (row: T) => ReactNode;
};

export function ApiResourceTable<T extends { id: number }>({
  title,
  fields,
  labelField,
  searchFields = [],
  list,
  create,
  update,
  remove,
  removeChild,
  lookupOptions: staticLookupOptions = {},
  lookupLoaders = {},
  lookupSearchLoaders = {},
  defaultValues = {},
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  enableCsvExport = true,
  csvFileName,
  editOnlyFields,
  statusFilterOptions,
  initialStatusFilter = "all",
  totalField,
  renderForm,
  buildPayload: buildPayloadOverride,
  mapRecordToForm,
  getDialogTitle,
  dialogClassName,
  addButtonLabel,
  toolbarActions,
  onView,
  renderRowActions,
  extraColumns = [],
  loadRecordForEdit,
  enableDateFilter = false,
  renderExpandableRow,
}: ApiResourceTableProps<T>) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [pagination, setPagination] = useState<ApiPagination>({
    count: 0,
    current_page: 1,
    total_pages: 1,
    next: null,
    previous: null,
  });
  const [editing, setEditing] = useState<Partial<T> | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [loadedLookups, setLoadedLookups] = useState<
    Record<string, LookupOption[]>
  >({});
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    label: string;
    parentId?: number;
    parent?: T;
    child?: T;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    title?: string;
  } | null>(null);
  const listRef = useRef(list);
  const debouncedSearchRef = useRef(debouncedSearch);

  listRef.current = list;
  debouncedSearchRef.current = debouncedSearch;

  const lookupOptions = useMemo(
    () => ({ ...staticLookupOptions, ...loadedLookups }),
    [staticLookupOptions, loadedLookups]
  );

  const editingId = (editing as Record<string, unknown> | null)?.id as
    | number
    | undefined;
  const isEditMode = editingId !== undefined;

  const isFieldDisabled = (f: Field): boolean => {
    if (f.readOnly) {
      return true;
    }
    if (isEditMode && editOnlyFields?.length) {
      return !editOnlyFields.includes(f.name);
    }
    return false;
  };

  const shouldIncludeInPayload = (f: Field): boolean => {
    if (!isEditMode || !editOnlyFields?.length) {
      return true;
    }
    return editOnlyFields.includes(f.name);
  };

  const tableFields = useMemo(() => {
    const explicit = fields.filter(f => f.showInTable);
    if (explicit.length > 0) {
      return explicit;
    }
    const visible = fields.filter(f => !f.hidden);
    return visible.filter(f => !f.hideInTable).slice(0, 5);
  }, [fields]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      if (next !== debouncedSearchRef.current) {
        setPage(1);
      }
      setDebouncedSearch(next);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  const fetchRows = async (targetPage = page) => {
    setLoading(true);
    try {
      const result = await listRef.current({
        page: targetPage,
        page_size: pageSize,
        search: debouncedSearch || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      setRows(result.data ?? []);
      setPagination(result.pagination);
      if (result.pagination.current_page !== targetPage) {
        setPage(result.pagination.current_page);
      }
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, `Failed to load ${title.toLowerCase()} records`)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;

    const run = async () => {
      setLoading(true);
      try {
        const result = await listRef.current({
          page,
          page_size: pageSize,
          search: debouncedSearch || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        });
        if (ignore) {
          return;
        }
        setRows(result.data ?? []);
        setPagination(result.pagination);
        if (result.pagination.current_page !== page) {
          setPage(result.pagination.current_page);
        }
      } catch (err) {
        if (!ignore) {
          projectToast.error(
            getApiErrorMessage(
              err,
              `Failed to load ${title.toLowerCase()} records`
            )
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      ignore = true;
    };
  }, [page, pageSize, debouncedSearch, statusFilter, startDate, endDate, title]);

  const total = useMemo(
    () =>
      totalField
        ? rows.reduce(
            (sum, row) =>
              sum + Number((row as Record<string, unknown>)[totalField] ?? 0),
            0
          )
        : 0,
    [rows, totalField]
  );

  const handlePageSizeChange = (size: number) => {
    if (size === pageSize) {
      return;
    }
    setPageSize(size);
    setPage(1);
  };

  const loadLookups = async () => {
    const loaders = Object.entries(lookupLoaders).filter(
      ([key]) => !lookupSearchLoaders[key]
    );
    if (loaders.length === 0) {
      return;
    }
    try {
      const entries = await Promise.all(
        loaders.map(async ([key, loader]) => [key, await loader()] as const)
      );
      setLoadedLookups(Object.fromEntries(entries));
    } catch (err) {
      projectToast.error(
        getApiErrorMessage(err, "Failed to load form options")
      );
    }
  };

  const startNew = () => {
    setEditing({ ...defaultValues } as Partial<T>);
    void loadLookups();
    setOpen(true);
  };

  const startEdit = (r: T) => {
    setEditing(mapRecordToForm ? mapRecordToForm(r) : r);
    void loadLookups();
    setOpen(true);
    if (!loadRecordForEdit) {
      return;
    }
    setFormLoading(true);
    void loadRecordForEdit(r.id)
      .then(record => {
        if (record) {
          setEditing(mapRecordToForm ? mapRecordToForm(record) : record);
        }
      })
      .catch(err => {
        projectToast.error(
          getApiErrorMessage(
            err,
            `Failed to load ${title.toLowerCase()} details`
          )
        );
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  const getRowLabel = (row: T): string => {
    const nameFieldKey =
      labelField ??
      fields.find(f => f.name === "name")?.name ??
      tableFields[0]?.name;
    if (!nameFieldKey) {
      return `${title} #${row.id}`;
    }
    const value = (row as Record<string, unknown>)[nameFieldKey];
    return value ? String(value) : `${title} #${row.id}`;
  };

  const requestDelete = (row: T, parent?: T) => {
    setDeleteTarget({
      id: row.id,
      label: getRowLabel(row),
      parentId: parent?.id,
      parent,
      child: parent ? row : undefined,
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    try {
      if (
        deleteTarget.parentId !== undefined &&
        deleteTarget.parent &&
        deleteTarget.child &&
        removeChild
      ) {
        await removeChild(
          deleteTarget.parentId,
          deleteTarget.id,
          deleteTarget.parent,
          deleteTarget.child
        );
      } else if (remove) {
        await remove(deleteTarget.id);
      } else {
        throw new Error("Deleting is not available.");
      }
      projectToast.success("Deleted");
      setDeleteTarget(null);
      void fetchRows(page);
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Could not delete record"));
    } finally {
      setDeleting(false);
    }
  };

  const applyFieldChange = (name: string, value: unknown) => {
    const updates = { ...(editing ?? {}), [name]: value } as Partial<T>;
    const field = fields.find(f => f.name === name);
    if (field?.linkedField && value !== undefined && value !== "") {
      const mapped = field.linkedField.valueMap[String(value)];
      if (mapped) {
        (updates as Record<string, unknown>)[field.linkedField.target] = mapped;
      }
    }
    setEditing(updates);
  };

  const buildPayload = (): Record<string, unknown> | null => {
    if (!editing) {
      return null;
    }
    const payload: Record<string, unknown> = {};
    for (const f of fields) {
      if (!shouldIncludeInPayload(f)) {
        continue;
      }
      const v = (editing as Record<string, unknown>)[f.name];
      if (f.type === "boolean") {
        payload[f.name] = v === true || v === "true";
        continue;
      }
      if (v === "" || v === undefined) {
        continue;
      }
      if (f.lookup || f.type === "select") {
        payload[f.name] = Number.isNaN(Number(v)) ? v : Number(v);
      } else if (f.type === "number" || f.type === "rating") {
        payload[f.name] = Number(v);
      } else if (f.type === "tags") {
        payload[f.name] = Array.isArray(v)
          ? v.map(item => String(item).trim()).filter(Boolean)
          : [];
      } else {
        payload[f.name] = v;
      }
    }
    if ((editing as Record<string, unknown>).image instanceof File) {
      payload.image = (editing as Record<string, unknown>).image;
    }
    return payload;
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) {
      return;
    }
    const payload = buildPayloadOverride
      ? buildPayloadOverride(editing)
      : buildPayload();
    if (!payload) {
      return;
    }

    if (!buildPayloadOverride) {
      for (const f of fields) {
        if (!shouldIncludeInPayload(f)) {
          continue;
        }
        if (
          f.required &&
          (payload[f.name] === undefined || payload[f.name] === "")
        ) {
          projectToast.error(`${f.label} is required`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      const id = (editing as Record<string, unknown>).id as number | undefined;
      if (id) {
        if (!update) {
          throw new Error("Editing is not available.");
        }
        await update(id, payload);
      } else {
        if (!create) {
          throw new Error("Creating is not available.");
        }
        await create(payload);
      }
      projectToast.success(id ? "Updated" : "Created");
      setOpen(false);
      setEditing(null);
      void fetchRows(page);
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Could not save record"));
    } finally {
      setSaving(false);
    }
  };

  const formatCellValue = (value: unknown): ReactNode => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const resolveDisplay = (f: Field, row: T): ReactNode => {
    let v = (row as Record<string, unknown>)[f.name];
    if (
      (v === null || v === undefined || v === "") &&
      f.name.endsWith("_name")
    ) {
      const baseKey = f.name.slice(0, -5);
      const baseVal = (row as Record<string, unknown>)[baseKey];
      if (baseVal !== null && baseVal !== undefined && baseVal !== "") {
        v = baseVal;
      }
    }
    if (
      (v === null || v === undefined || v === "") &&
      f.name === "category_name"
    ) {
      v =
        (row as Record<string, unknown>).main_category_name ??
        (row as Record<string, unknown>).category;
    }
    if (
      (v === null || v === undefined || v === "") &&
      f.name === "type_of_donation_name"
    ) {
      v =
        (row as Record<string, unknown>).donation_type_name ??
        (row as Record<string, unknown>).type_of_donation;
    }
    if (
      (v === null || v === undefined || v === "") &&
      f.name === "sub_category_title"
    ) {
      v =
        (row as Record<string, unknown>).sub_category_name ??
        (row as Record<string, unknown>).sub_category;
    }
    if (f.name === "date_status") {
      const statusValue =
        v === null || v === undefined || v === "" ? null : String(v);
      if (!statusValue) {
        return "—";
      }
      const label = statusValue.replace(/_/g, " ");
      const badgeClass =
        statusValue === "live"
          ? "bg-amber-500/15 text-amber-800 hover:bg-amber-500/15"
          : statusValue === "upcoming"
            ? "bg-blue-500/15 text-blue-700 hover:bg-blue-500/15"
            : statusValue === "completed" || statusValue === "closed"
              ? "bg-muted text-muted-foreground hover:bg-muted"
              : "bg-muted text-muted-foreground hover:bg-muted";
      return <Badge className={`${badgeClass} capitalize`}>{label}</Badge>;
    }
    if (f.lookup) {
      const nameField = `${f.name}_name`;
      const nameVal = (row as Record<string, unknown>)[nameField];
      if (nameVal) {
        return String(nameVal);
      }
      const hit = lookupOptions[f.name]?.find(o => o.value === String(v));
      return hit
        ? hit.secondary
          ? `${hit.label} (${hit.secondary})`
          : hit.label
        : formatCellValue(v);
    }
    if (f.name === "category" && (row as Record<string, unknown>).category_name) {
      return String((row as Record<string, unknown>).category_name);
    }
    if (f.type === "image" || f.name === "image_url") {
      const imgVal =
        v ??
        (row as Record<string, unknown>).image_url ??
        (row as Record<string, unknown>).image;
      if (!imgVal || typeof imgVal !== "string" || imgVal === "—") {
        return <span className="text-xs italic text-muted-foreground">No image</span>;
      }
      const fullUrl = resolveMediaUrl(imgVal);
      if (!fullUrl) {
        return <span className="text-xs italic text-muted-foreground">No image</span>;
      }
      const itemTitle = String(
        (row as Record<string, unknown>)[labelField ?? "title"] ??
          (row as Record<string, unknown>).title ??
          "Image Preview"
      );
      return (
        <div
          className="group relative h-11 w-14 overflow-hidden rounded-lg border border-border bg-muted/40 cursor-pointer shadow-2xs transition-all hover:scale-105 hover:ring-2 hover:ring-primary/60"
          onClick={e => {
            e.stopPropagation();
            setPreviewImage({ url: fullUrl, title: itemTitle });
          }}
          title="Click to view full image"
        >
          <img
            src={fullUrl}
            alt={itemTitle}
            className="h-full w-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/35 opacity-0 transition-opacity group-hover:opacity-100">
            <Eye className="h-4 w-4 text-white drop-shadow" />
          </div>
        </div>
      );
    }
    if (f.type === "boolean") {
      if (f.name === "is_active") {
        return v === true ? (
          <Badge className="bg-amber-500/15 text-amber-800 hover:bg-amber-500/15">
            Active
          </Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        );
      }
      return v === true ? (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          Yes
        </Badge>
      ) : (
        <Badge variant="secondary">No</Badge>
      );
    }
    if (f.currency || (f.type === "number" && /amount/i.test(f.name))) {
      const amount = Number(v ?? 0);
      return Number.isFinite(amount)
        ? `₹${amount.toLocaleString("en-IN")}`
        : "—";
    }
    if (f.type === "rating") {
      return <StarRating value={Number(v) || 0} readOnly size="sm" />;
    }
    if (f.type === "tags") {
      const tags = Array.isArray(v)
        ? v.map(item => String(item).trim()).filter(Boolean)
        : typeof v === "string" && v.trim()
          ? v
              .split(",")
              .map(item => item.trim())
              .filter(Boolean)
          : [];
      if (!tags.length) {
        return "—";
      }
      return (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((t, idx) => (
            <span
              key={`tag-${idx}-${t}`}
              className="rounded-md border border-amber-100/50 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900"
            >
              {f.currency === true || f.name === "suggested_amounts"
                ? `₹${isNaN(Number(t)) ? t : Number(t).toLocaleString("en-IN")}`
                : t}
            </span>
          ))}
        </div>
      );
    }
    if (f.name === "status") {
      const statusValue =
        v === null || v === undefined || v === "" ? null : String(v);
      if (!statusValue) {
        return "—";
      }
      const animalStatuses = ["active", "non_productive", "deceased", "death", "inactive", "sold"];
      if (animalStatuses.includes(statusValue.toLowerCase())) {
        const label =
          f.options?.find(o => o.value === statusValue)?.label ?? statusValue;
        const badgeClass =
          statusValue === "active"
            ? "bg-green-500/10 text-green-700 hover:bg-green-500/10 border border-green-200"
            : statusValue === "deceased" || statusValue === "death"
              ? "bg-red-500/10 text-red-700 hover:bg-red-500/10 border border-red-200"
              : statusValue === "non_productive"
                ? "bg-amber-500/10 text-amber-700 hover:bg-amber-500/10 border border-amber-200"
                : "bg-slate-500/10 text-slate-700 hover:bg-slate-500/10 border border-slate-200";

        if (!update) {
          return (
            <Badge className={`${badgeClass} font-semibold shadow-none capitalize`}>
              {label}
            </Badge>
          );
        }

        return (
          <Select
            value={statusValue}
            onValueChange={async (newVal) => {
              if (newVal === statusValue) return;
              try {
                await update(row.id, { [f.name]: newVal });
                projectToast.success("Status updated successfully");
                void fetchRows(page);
              } catch (err) {
                console.error(err);
                projectToast.error("Failed to update status");
              }
            }}
          >
            <SelectTrigger className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none border shadow-none cursor-pointer h-fit w-fit [&>svg]:opacity-50 [&>svg]:ml-1", badgeClass)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {f.options?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      const paymentStatuses = [
        "success",
        "pending",
        "failed",
        "cancelled",
        "canceled",
        "captured",
        "created",
      ];
      if (paymentStatuses.includes(statusValue.toLowerCase())) {
        const label =
          f.options?.find(o => o.value === statusValue)?.label ??
          formatPaymentStatusLabel(statusValue);
        return (
          <Badge
            className={`capitalize ${paymentStatusBadgeClass(statusValue)}`}
          >
            {label}
          </Badge>
        );
      }
      const label =
        f.options?.find(o => o.value === statusValue)?.label ??
        formatDonationRequestStatusLabel(statusValue) ??
        formatFeedbackStatusLabel(statusValue) ??
        formatContactFormStatusLabel(statusValue);
      const badgeClass = isDonationRequestStatus(statusValue)
        ? donationRequestStatusClass(statusValue)
        : ["open", "in_progress", "closed"].includes(statusValue)
          ? contactFormStatusClass(statusValue)
          : feedbackStatusClass(statusValue);
      return <Badge className={badgeClass}>{label}</Badge>;
    }
    if (f.type === "date" || /(_date|_at)$/.test(f.name)) {
      return v ? formatDate(String(v)) : "—";
    }
    if (f.type === "select" && f.options?.length) {
      const hit = f.options.find(o => o.value === String(v));
      if (hit) {
        return hit.label;
      }
    }
    return formatCellValue(v);
  };

  const getChildRows = (row: T): T[] => {
    const children = (row as Record<string, unknown>).children;
    return Array.isArray(children)
      ? (children.filter(
          child =>
            child &&
            typeof child === "object" &&
            typeof (child as { id?: unknown }).id === "number"
        ) as T[])
      : [];
  };

  const toggleExpandedRow = (rowId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const renderCellContent = (
    field: Field,
    row: T,
    isChild: boolean,
    childCount = 0,
    isExpanded = false
  ): ReactNode => {
    const content = resolveDisplay(field, row);
    const isPrimaryField = field.name === tableFields[0]?.name;

    if (isChild && isPrimaryField) {
      return (
        <div className="flex min-w-0 items-center gap-3 pl-5">
          <span className="h-px w-5 shrink-0 bg-border" />
          <div className="min-w-0">
            <div className="truncate font-medium">{content}</div>
            <Badge
              variant="secondary"
              className="bg-primary/10 hover:bg-primary/10 mt-1 text-primary"
            >
              Child
            </Badge>
          </div>
        </div>
      );
    }

    if (!isChild && isPrimaryField && (childCount > 0 || renderExpandableRow)) {
      return (
        <div className="flex min-w-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 rounded-full"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Hide details" : "Show details"}
            onClick={event => {
              event.stopPropagation();
              toggleExpandedRow(row.id);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
          <div className="min-w-0">
            <div className="truncate font-medium">{content}</div>
            {!renderExpandableRow && (
              <div className="mt-1 flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                  Head
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {childCount} child{childCount > 1 ? "ren" : ""}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return content;
  };

  const renderActions = (row: T, parent?: T) => (
    <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
      {renderRowActions?.(row, {
        refresh: () => void fetchRows(page),
      })}
      {update && !parent && (
        <Button size="icon" variant="ghost" onClick={() => startEdit(row)}>
          <Pencil className="h-4 w-4" />
        </Button>
      )}
      {onView && !parent && (
        <Button
          size="icon"
          variant="ghost"
          className="text-primary"
          title="View"
          aria-label={`View ${String((row as Record<string, unknown>)[labelField ?? "id"] ?? row.id)}`}
          onClick={() => onView(row)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      )}
      {(parent ? removeChild : remove) && (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => requestDelete(row, parent)}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      )}
    </div>
  );

  const refreshRows = () => void fetchRows(page);
  const showActions = Boolean(
    update ?? remove ?? removeChild ?? onView ?? renderRowActions
  );
  const colSpan =
    tableFields.length + extraColumns.length + (showActions ? 1 : 0);

  const renderExtraColumnCells = (row: T) =>
    extraColumns.map(column => (
      <TableCell
        key={column.id}
        className={cn("px-3 py-2.5", column.cellClassName)}
        onClick={e => e.stopPropagation()}
      >
        {column.render(row, { refresh: refreshRows })}
      </TableCell>
    ));

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center">
        <div className="relative sm:min-w-[180px] sm:max-w-sm sm:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={`Search ${searchFields.join(", ") || "records"}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-muted/40 rounded-xl border-transparent pl-9"
          />
        </div>
        {search && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setSearch("")}
            className="rounded-xl"
          >
            Clear
          </Button>
        )}
        {statusFilterOptions && statusFilterOptions.length > 0 && (
          <Select
            value={statusFilter}
            onValueChange={value => {
              setStatusFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="bg-muted/40 w-full rounded-xl border-transparent sm:w-40">
              <SelectValue placeholder="All status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              {statusFilterOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {enableDateFilter && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground font-medium">From</span>
              <Input
                type="date"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                className="bg-muted/40 h-9 rounded-xl border-transparent text-xs pl-3 pr-2 py-1 w-[150px] font-sans"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground font-medium">To</span>
              <Input
                type="date"
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                className="bg-muted/40 h-9 rounded-xl border-transparent text-xs pl-3 pr-2 py-1 w-[150px] font-sans"
              />
            </div>
            {(startDate || endDate) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setPage(1);
                }}
                className="h-8 text-xs font-semibold rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10 px-2.5"
              >
                Reset Dates
              </Button>
            )}
          </div>
        )}
        {totalField && (
          <div className="text-sm text-muted-foreground">
            Total:{" "}
            <span className="font-bold text-foreground">
              ₹{total.toLocaleString("en-IN")}
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          {toolbarActions}
          {enableCsvExport && (
            <CsvExportButton
              exporting={exportingCsv}
              className="flex-1 sm:flex-none"
              onExport={async () => {
                setExportingCsv(true);
                try {
                  const exportRows = await fetchAllListData(listRef.current, {
                    search: debouncedSearch || undefined,
                    status: statusFilter !== "all" ? statusFilter : undefined,
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                  });
                  if (!exportRows.length) {
                    projectToast.error("Nothing to export");
                    return;
                  }
                  exportApiRowsToCsv(
                    `${csvFileName ?? title.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}`,
                    exportRows as Record<string, unknown>[]
                  );
                  projectToast.success(`Exported ${exportRows.length} rows`);
                } catch (err) {
                  projectToast.error(
                    getApiErrorMessage(err, "Failed to export CSV")
                  );
                } finally {
                  setExportingCsv(false);
                }
              }}
            />
          )}
          {(create ?? update) && (
            <Dialog
              open={open}
              onOpenChange={v => {
                setOpen(v);
                if (!v) {
                  setEditing(null);
                  setFormLoading(false);
                }
              }}
            >
              {create ? (
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    onClick={startNew}
                    className="flex-1 gap-1 rounded-xl sm:flex-none"
                  >
                    <Plus className="h-4 w-4" />{" "}
                    {addButtonLabel ?? `Add ${title}`}
                  </Button>
                </DialogTrigger>
              ) : null}
              <DialogContent
                className={cn(
                  "flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl bg-background p-0",
                  dialogClassName
                )}
              >
                <DialogHeader className="shrink-0 border-b border-border px-4 py-4 pr-12 text-left sm:px-6">
                  <DialogTitle>
                    {getDialogTitle
                      ? getDialogTitle(editing, title)
                      : (editing as Record<string, unknown>)?.id
                        ? `Edit ${title}`
                        : `New ${title}`}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={save} className="flex min-h-0 flex-1 flex-col">
                  <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
                    {formLoading ? (
                      <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                        Loading…
                      </div>
                    ) : renderForm ? (
                      renderForm({ editing, setEditing, isEditMode })
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {fields
                          .filter(f => !f.hidden)
                          .map(f => {
                            const disabled = isFieldDisabled(f);
                            return (
                              <div
                                key={f.name}
                                className={
                                  f.type === "textarea" ||
                                  f.type === "rating" ||
                                  f.type === "tags"
                                    ? "sm:col-span-2"
                                    : ""
                                }
                              >
                                <Label
                                  data-required={
                                    f.required ? "true" : undefined
                                  }
                                >
                                  {f.label}
                                </Label>
                                {f.lookup ? (
                                  <SearchableSelect
                                    value={String(
                                      (editing as Record<string, unknown>)?.[
                                        f.name
                                      ] ?? ""
                                    )}
                                    onValueChange={v =>
                                      applyFieldChange(f.name, v)
                                    }
                                    options={lookupOptions[f.name]}
                                    onSearch={lookupSearchLoaders[f.name]}
                                    disabled={disabled}
                                    seedOption={
                                      (editing as Record<string, unknown>)?.[
                                        f.name
                                      ] &&
                                      (editing as Record<string, unknown>)?.[
                                        `${f.name}_name`
                                      ]
                                        ? {
                                            value: String(
                                              (
                                                editing as Record<
                                                  string,
                                                  unknown
                                                >
                                              )[f.name]
                                            ),
                                            label: String(
                                              (
                                                editing as Record<
                                                  string,
                                                  unknown
                                                >
                                              )[`${f.name}_name`]
                                            ),
                                          }
                                        : undefined
                                    }
                                    placeholder={
                                      f.placeholder ?? `Select ${f.label}...`
                                    }
                                    searchPlaceholder={`Search ${f.label.toLowerCase()}...`}
                                    emptyText={`No ${f.label.toLowerCase()} found.`}
                                  />
                                ) : f.type === "textarea" ? (
                                  <Textarea
                                    value={String(
                                      (editing as Record<string, unknown>)?.[
                                        f.name
                                      ] ?? ""
                                    )}
                                    onChange={e =>
                                      setEditing({
                                        ...(editing ?? {}),
                                        [f.name]: e.target.value,
                                      } as Partial<T>)
                                    }
                                    readOnly={disabled}
                                    disabled={disabled}
                                    rows={3}
                                    className={cn(
                                      "bg-muted/40 rounded-xl border-transparent",
                                      disabled && "cursor-default opacity-80"
                                    )}
                                  />
                                ) : f.type === "select" ? (
                                  <SearchableSelect
                                    value={String(
                                      (editing as Record<string, unknown>)?.[
                                        f.name
                                      ] ?? ""
                                    )}
                                    onValueChange={v =>
                                      applyFieldChange(f.name, v)
                                    }
                                    options={
                                      lookupOptions[f.name] ?? f.options ?? []
                                    }
                                    placeholder={f.placeholder ?? "Select..."}
                                    searchPlaceholder={`Search ${f.label.toLowerCase()}...`}
                                    emptyText={`No ${f.label.toLowerCase()} found.`}
                                    disabled={disabled}
                                    className={cn(
                                      disabled && "cursor-default opacity-80"
                                    )}
                                  />
                                ) : f.type === "rating" ? (
                                  <StarRating
                                    value={
                                      Number(
                                        (editing as Record<string, unknown>)?.[
                                          f.name
                                        ]
                                      ) || 0
                                    }
                                    onChange={v => applyFieldChange(f.name, v)}
                                    disabled={disabled}
                                  />
                                ) : f.type === "tags" ? (
                                  <TagsInput
                                    value={
                                      ((editing as Record<string, unknown>)?.[
                                        f.name
                                      ] as
                                        | (string | number)[]
                                        | string
                                        | null) ?? []
                                    }
                                    onChange={next =>
                                      applyFieldChange(f.name, next)
                                    }
                                    placeholder={
                                      f.placeholder ?? "Type and press Enter…"
                                    }
                                    isCurrency={
                                      f.currency === true ||
                                      f.name === "suggested_amounts"
                                    }
                                  />
                                ) : f.type === "boolean" ? (
                                  <div className="bg-muted/40 flex h-11 items-center gap-3 rounded-xl px-3">
                                    <Switch
                                      checked={
                                        (editing as Record<string, unknown>)?.[
                                          f.name
                                        ] === true
                                      }
                                      disabled={disabled}
                                      onCheckedChange={(v: boolean) =>
                                        setEditing({
                                          ...(editing ?? {}),
                                          [f.name]: v,
                                        } as Partial<T>)
                                      }
                                    />
                                    <span className="text-sm text-muted-foreground">
                                      {(editing as Record<string, unknown>)?.[
                                        f.name
                                      ] === true
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  </div>
                                ) : (
                                  <Input
                                    type={f.type ?? "text"}
                                    required={f.required && !disabled}
                                    readOnly={disabled}
                                    disabled={disabled}
                                    value={String(
                                      (editing as Record<string, unknown>)?.[
                                        f.name
                                      ] ?? ""
                                    )}
                                    onChange={e =>
                                      applyFieldChange(f.name, e.target.value)
                                    }
                                    placeholder={f.placeholder}
                                    className={cn(
                                      "bg-muted/40 rounded-xl border-transparent",
                                      disabled && "cursor-default opacity-80"
                                    )}
                                  />
                                )}
                                {f.help && (
                                  <p className="mt-1 text-[5px] text-muted-foreground">
                                    {f.help}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  <DialogFooter className="shrink-0 border-t border-border bg-background px-4 py-3 sm:px-6 sm:py-4">
                    <Button
                      type="submit"
                      disabled={saving || formLoading}
                      className="w-full rounded-xl sm:w-auto"
                    >
                      {saving ? "Saving…" : "Save"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="-mx-3 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm sm:mx-0">
        <Table className="[&_td]:border-border/70 min-w-[640px] border-collapse [&_td]:border [&_th]:border [&_th]:border-border">
          <TableHeader className="bg-muted/60">
            <TableRow className="hover:bg-muted/60">
              {tableFields.map(f => (
                <TableHead
                  key={f.name}
                  className="px-3 py-2.5 font-semibold text-foreground"
                >
                  {f.label}
                </TableHead>
              ))}
              {extraColumns.map(column => (
                <TableHead
                  key={column.id}
                  className={cn(
                    "w-24 px-3 py-2.5 text-center font-semibold text-foreground",
                    column.headerClassName
                  )}
                >
                  {column.label}
                </TableHead>
              ))}
              {showActions && (
                <TableHead className="w-40 px-3 py-2.5 text-right font-semibold text-foreground">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="py-10 text-center text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colSpan}
                  className="py-10 text-center text-muted-foreground"
                >
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r, i) => {
                const children = getChildRows(r);
                const isExpanded = expandedRows.has(r.id);
                return (
                  <Fragment key={r.id}>
                    <TableRow
                      className={cn(
                        i % 2 === 0 ? "bg-background" : "bg-muted/20",
                        (children.length > 0 || renderExpandableRow) && "cursor-pointer"
                      )}
                      onClick={() => {
                        if (children.length > 0 || renderExpandableRow) {
                          toggleExpandedRow(r.id);
                        }
                      }}
                    >
                      {tableFields.map(f => (
                        <TableCell
                          key={f.name}
                          className="max-w-xs truncate px-3 py-2.5"
                        >
                          {renderCellContent(
                            f,
                            r,
                            false,
                            children.length,
                            isExpanded
                          )}
                        </TableCell>
                      ))}
                      {renderExtraColumnCells(r)}
                      {showActions && (
                        <TableCell className="px-3 py-2.5 text-right">
                          {renderActions(r)}
                        </TableCell>
                      )}
                    </TableRow>

                    {isExpanded &&
                      (renderExpandableRow ? (
                        <TableRow className="bg-muted/5 hover:bg-muted/5 border-t border-b border-border/80">
                          <TableCell colSpan={colSpan} className="p-4">
                            {renderExpandableRow(r)}
                          </TableCell>
                        </TableRow>
                      ) : (
                        children.map(child => (
                          <TableRow
                            key={`${r.id}-child-${child.id}`}
                            className="bg-primary/5 hover:bg-primary/10"
                          >
                            {tableFields.map(f => (
                              <TableCell
                                key={f.name}
                                className="max-w-xs truncate px-3 py-2.5 text-sm"
                              >
                                {renderCellContent(f, child, true)}
                              </TableCell>
                            ))}
                            {renderExtraColumnCells(child)}
                            {showActions && (
                              <TableCell className="px-3 py-2.5 text-right">
                                {renderActions(child, r)}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      ))}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={pagination.count}
        totalPages={pagination.total_pages}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={open => {
          if (!open && !deleting) {
            setDeleteTarget(null);
          }
        }}
        title={
          deleteTarget?.parentId !== undefined
            ? `Delete child ${title}?`
            : `Delete ${title}?`
        }
        description={
          deleteTarget?.parentId !== undefined
            ? `Are you sure you want to remove this child ${title.toLowerCase()} from the family head? This action cannot be undone.`
            : `Are you sure you want to delete this ${title.toLowerCase()}? This action cannot be undone.`
        }
        itemName={deleteTarget?.label}
        loading={deleting}
        onConfirm={confirmDelete}
      />

      {/* Image Preview Lightbox Dialog */}
      <Dialog
        open={!!previewImage}
        onOpenChange={open => {
          if (!open) setPreviewImage(null);
        }}
      >
        <DialogContent className="max-w-3xl overflow-hidden rounded-2xl p-3 sm:p-5">
          <DialogHeader className="border-border/60 border-b px-1 pb-2">
            <DialogTitle className="truncate text-base font-bold text-foreground">
              {previewImage?.title || "Image View"}
            </DialogTitle>
          </DialogHeader>
          {previewImage && (
            <div className="bg-muted/30 mt-3 flex max-h-[75vh] w-full items-center justify-center overflow-hidden rounded-xl p-1">
              <img
                src={previewImage.url}
                alt={previewImage.title || "Full view"}
                className="max-h-[70vh] w-auto max-w-full rounded-lg object-contain shadow-sm"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
